// WHAT: Meals Overview Component (Organism)
// WHY:  Displays all meals for the day with progress tracking and total summary
// DOMAIN: Health → Yazio
// DEPENDENCIES: MealCard molecule, MealsOverviewProps type
// RISKS:
//   - NOTE: Meal icons are hardcoded SVG (not from icon library)
//   - NOTE: Meal titles are hardcoded English strings (not internationalized)
//   - NOTE: Total calculation assumes all meals have calories and goal properties

import type { MealsOverviewProperties, MealCalories } from "../../types";

import { MealCard } from "../molecules/MealCard";

/**
 * WHAT:
 *   Organism component displaying all daily meals with summary statistics.
 *
 * WHY:
 *   Provides comprehensive meal tracking view with individual meal cards
 *   and aggregated daily totals.
 *
 * PROPS:
 *   - meals: object — Object with meal types as keys (breakfast, lunch, dinner, snack)
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Maps over meals object to render MealCard for each meal type
 *   - Uses hardcoded icons and titles for each meal type
 *   - Displays meals in responsive grid (1 col mobile, 2 col desktop)
 *   - Shows total daily intake summary card with calories consumed and goal
 *   - Calculates totals by summing all meal calories and goals
 *
 * RISKS:
 *   - NOTE: Meal icons are hardcoded SVG (not reusable or customizable)
 *   - NOTE: Meal titles are hardcoded English (not internationalized)
 *   - NOTE: Total calculation doesn't handle missing meal data gracefully
 *   - NOTE: Assumes meals object has all expected meal types
 *
 * @param root0
 *
 * @param root0.meals
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Meals overview component
 *
 */
export function MealsOverview({ className, meals }: Readonly<MealsOverviewProperties>) {
  const mealIcons = {
    breakfast: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    dinner: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    lunch: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    snack: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
  };

  const mealTitles = {
    breakfast: "Breakfast",
    dinner: "Dinner",
    lunch: "Lunch",
    snack: "Snacks",
  };

  const safeClassName: string | undefined = className ?? undefined;

  return (
    <div className={safeClassName}>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white mb-2">Meals Today</h2>
        <p className="text-slate-400">Track your daily meal progress and calorie intake</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(["breakfast", "lunch", "dinner", "snack"] as (keyof typeof mealTitles)[]).map(
          (mealType) => {
            const mealData = meals[mealType];
            return (
              <MealCard
                icon={mealIcons[mealType]}
                key={mealType}
                meal={mealData}
                title={mealTitles[mealType]}
              />
            );
          },
        )}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-white">Total Daily Intake</h3>
            <p className="text-sm text-slate-400">All meals combined</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">
              {(() => {
                let totalCalories = 0;
                const mealValues = Object.values(meals) as MealCalories[];
                for (const meal of mealValues) {
                  totalCalories += meal.calories;
                }
                return totalCalories.toFixed(0);
              })()}{" "}
              kcal
            </div>
            <div className="text-sm text-slate-400">
              of{" "}
              {(() => {
                let totalGoal = 0;
                const mealValues = Object.values(meals) as MealCalories[];
                for (const meal of mealValues) {
                  totalGoal += meal.goal;
                }
                return totalGoal.toFixed(0);
              })()}{" "}
              kcal goal
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
